// InstantMessageList - MODULE
// ***********************************************************************************************************************
import { Box, Chip, lighten } from "@mui/material";
import downloadjs from "downloadjs";
import * as React from "react";
import { ConfirmationDialogResult, UserType } from "ui/Scripts/source/models/enums";
import { IInstantMessageListItem } from "ui/Scripts/source/models/instantMessage/instantMessageListItem";
import { Translations } from "ui/Scripts/source/models/translations";
import { AppUtils } from "../../../framework/appUtils";
import * as attachmentService from "../../../models/services/attachmentService";
import * as instantMessageService from "../../../models/services/instantMessageService";
import MessageListLine from "../../framework/Message/MessageLine/MessageLine";

// InstantMessageListLine
export interface IInstantMessageListLineProp {
    observer: IntersectionObserver;
    channelId: string;
    channelIsReadOnly: boolean;
    userCode: string;
    userIsChannelAdmin: boolean;
    selectedId: string;
    item: IInstantMessageListItem;
    onSetSelected: (id: string) => void;
    onReplyToInstantMessage: (item: IInstantMessageListItem) => void;
}

export class InstantMessageListLine extends React.Component<IInstantMessageListLineProp, {}> {
    private lineDiv = React.createRef<HTMLDivElement>();

    componentDidMount(): void {
        const props = this.props;
        if (!props.observer || !this.lineDiv.current || !props.item || props.item.isTitle || props.item.modifyUser.code === props.userCode || props.item.modifyUser.isRemoved()) return;
        props.observer.observe(this.lineDiv.current);
    }

    componentWillUnmount(): void {
        const props = this.props;
        if (!props.observer || !this.lineDiv.current) return;
        props.observer.unobserve(this.lineDiv.current);
    }

    handleMessageClick = () => {
        const props = this.props;
        props.onSetSelected(props.item.id);
    };

    handleAttachmentClick = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const attachment = props.item.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    handleReplyClick = async () => {
        const props = this.props;
        props.onReplyToInstantMessage(props.item);
    };

    handleDeleteClick = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRemoveThisInstantMessage) === ConfirmationDialogResult.Yes) {
            // Call server
            AppUtils.callService(() => instantMessageService.removeInstantMessage(props.item.id));
        }
    };

    handleRestoreClick = async () => {
        const props = this.props;
        if (await AppUtils.showConfirmationDialog(Translations.DoYouReallyWantToRestoreThisInstantMessage) === ConfirmationDialogResult.Yes) {
            // Call server
            AppUtils.callService(() => instantMessageService.restoreInstantMessage(props.item.id));
        }
    };
    
    render() {
        const props = this.props;
        const item = props.item;
        if (item.isTitle) {
            return (
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                    <Chip variant="filled" label={item.content} sx={(theme) => ({ bgcolor: lighten(theme.palette.info.light, 0.9), color: theme.palette.getContrastText(lighten(theme.palette.info.light, 0.9)) })} />
                </Box>
            );
        }
        const isDeleted = item.isDeleted();
        const modifyUserIsDeleted = item.modifyUser.isRemoved();
        const isMyMessage = props.userCode === item.modifyUser.code;
        const replyUserIsDeleted = item.replyToInstantMessage ? item.replyToInstantMessage.modifyUser.userType === UserType.Removed : false;
        return (
            <MessageListLine
                ref={this.lineDiv}
                id={item.id}
                userCode={item.modifyUser.code}
                content={item.getText(props.userCode)}
                attachments={item.attachments}
                date={item.modifyUser.date}
                myMessage={isMyMessage}
                sender={item.modifyUser.name}
                someRead={item.isSomeRead()}
                allRead={item.isAllRead()}
                isNew={!isDeleted && !modifyUserIsDeleted && !isMyMessage && !item.isUserRead()}
                image={item.modifyUser.picture}
                readTooltip={item.isAllRead() ? Translations.AllInstantMessageUsersHaveReadMessage : Translations.SomeInstantMessageUsersHaveReadMessage}
                showAvatar
                isDeleted={isDeleted}
                senderDisabled={replyUserIsDeleted || modifyUserIsDeleted}
                senderTooltip={(replyUserIsDeleted || modifyUserIsDeleted) ? Translations.InstantMessageChannelUsersNotUsable : undefined}
                replySender={item.replyToInstantMessage?.modifyUser?.name}
                replyText={item.replyToInstantMessage?.getText(props?.userCode)}
                onClick={this.handleMessageClick}
                onRemove={(!isDeleted && (isMyMessage || props.userIsChannelAdmin) && !props.channelIsReadOnly) ? this.handleDeleteClick : null}
                onRestore={(isDeleted && (props.userCode === item.deleteUser.code || props.userIsChannelAdmin) && !props.channelIsReadOnly) ? this.handleRestoreClick : null}
                onReply={!props.channelIsReadOnly ? this.handleReplyClick : null}
            />
        );
    }
}